import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICondoDateRange, IQuote } from '@share/common-types';
import { AppThunk, isSpanish, Toaster } from '@share/utils';
import { QuotesAPI } from '@share/api';
import moment, { Moment } from 'moment';

export interface IQuoteState {
  loading: boolean;
  errorHotels: string;
  errorCondos: string;
  result: {
    quotesHotels: IQuote[];
    quotesCondos: IQuote[];
    quotesCars: IQuote[];
    quotesHistorical: IQuote[];
    selectedQuotes: IQuote[];
  }
}

const initialState: IQuoteState = {
  loading: true,
  errorHotels: '',
  errorCondos: '',
  result: {
    quotesHotels: [],
    quotesCondos: [],
    quotesCars: [],
    quotesHistorical: [],
    selectedQuotes: []
  }
};

const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    setLoading: (state: IQuoteState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setErrorHotels: (state: IQuoteState, { payload }: PayloadAction<string>) => {
      state.errorHotels = payload;
    },
    setErrorCondos: (state: IQuoteState, { payload }: PayloadAction<string>) => {
      state.errorCondos = payload;
    },
    setQuotesHotels: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesHotels = payload;
    },
    setQuotesCondos: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesCondos = payload;
    },
    setQuotesCars: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesCars = payload;
    },
    setQuotesHistorical: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesHistorical = payload;
    },
    setSelectedQuotes: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.selectedQuotes = payload;
    }
  },
});

export const quotesActions = quotesSlice.actions;
export const quotesReducer = quotesSlice.reducer;

const isValidCheckIn = (checkIn: string, today: Moment) => 
                              moment(checkIn, 'yyyy-MM-DD').isAfter(today);

const isValidAvailableDates = (availableDates: ICondoDateRange[], today: Moment, d: any) => availableDates?.some(a => moment(a.from, 'yyyy-MM-DD').isAfter(today));

const isValidCondoQuote = (d: IQuote, todayMoment: Moment) => 
                                d.quoteType === 'Condo' && d.isCondo && isValidAvailableDates(d.availableDates || [], todayMoment, d);

const isValidHotelQuote = (d: IQuote, todayMoment: Moment) => 
                                d.quoteType === 'Hotel' && !d.isCondo && isValidCheckIn(d.checkIn, todayMoment);

const isValidCarQuote = (d: IQuote, todayMoment: Moment) => 
                                d.quoteType === 'Car' && isValidCheckIn(d.checkIn, todayMoment);

const isValidHistoricalQuote = (d: IQuote, todayMoment: Moment) => 
                                    !isValidCondoQuote(d, todayMoment) && 
                                    !isValidHotelQuote(d, todayMoment) && 
                                    !isValidCarQuote(d, todayMoment);

export const getQuotes = (id?: any): AppThunk => {
  return async (dispatch) => {
    dispatch(quotesActions.setLoading(true));
    dispatch(quotesActions.setQuotesHotels([]));
    dispatch(quotesActions.setQuotesCondos([]));
    dispatch(quotesActions.setQuotesCars([]));
    dispatch(quotesActions.setQuotesHistorical([]));

    dispatch(quotesActions.setSelectedQuotes([]));

    try {
      const response = await QuotesAPI.get(null);
      if (response.status === 200) {
        const { data } = response;
        const todayMoment = moment();
        const hotels = data?.length? [...data.filter((d: IQuote) => isValidHotelQuote(d, todayMoment))]: [];
        const condos = data?.length ? [...data.filter((d: IQuote) => isValidCondoQuote(d, todayMoment))] : [];
        const cars = data?.length ? [...data.filter((d: IQuote) => isValidCarQuote(d, todayMoment))] : [];
        const historical = data?.length ? [...data.filter((d: IQuote) => isValidHistoricalQuote(d, todayMoment))] : [];

        console.log('data', data);
        console.log('hotels', hotels);
        console.log('condos', condos);
        console.log('cars', cars);
        console.log('historical', historical);

        dispatch(quotesActions.setQuotesHotels(hotels));
        dispatch(quotesActions.setQuotesCondos(condos));
        dispatch(quotesActions.setQuotesCars(cars));
        dispatch(quotesActions.setQuotesHistorical(historical));

        if (id) {
          dispatch(quotesActions.setSelectedQuotes(data?.filter((d: IQuote) => d.quoteBaseId === id) || []));
        }
      } else {
        dispatch(quotesActions.setErrorHotels(`[${response.status}] ${response.statusText}`));
        Toaster.error(!isSpanish() ? `An error occurred. [${response.status}] ${response.statusText}. If the problem persists, please contact the system administrator.`
      : `Ha ocurrido un error. [${response.status}] ${response.statusText}. Si el problema persiste, por favor contacte al administrador del sistema.`);
      }
    } catch (error: any) {
      console.error(error);
      dispatch(quotesActions.setErrorHotels(error.toString()));
      Toaster.error(!isSpanish() ? `An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`
    : `Ha ocurrido un error. ${error.toString()}. Si el problema persiste, por favor contacte al administrador del sistema.`);
    }

    dispatch(quotesActions.setLoading(false));
  };
};


export const markQuotesAsRead = (quoteId?: any): AppThunk => {
  return async () => {
    try {
      await QuotesAPI.markQuotesAsRead(quoteId);
    } catch (error: any) {
      console.error(error);
    }
  };
};